import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Technicians from './pages/Technicians';
import TechnicianForm from './pages/TechnicianForm';
import TechnicianView from './pages/TechnicianView';
import Customers from './pages/Customers';
import CustomerForm from './pages/CustomerForm';
import CustomerView from './pages/CustomerView';
import Helpers from './pages/Helpers';
import HelperForm from './pages/HelperForm';
import HelperView from './pages/HelperView';
import Inventory from './pages/Inventory';
import InventoryForm from './pages/InventoryForm';
import Tools from './pages/Tools';
import ToolForm from './pages/ToolForm';
import SiteVisits from './pages/SiteVisits';
import SiteVisitForm from './pages/SiteVisitForm';
import SiteVisitView from './pages/SiteVisitView';
import CompleteSiteVisit from './pages/CompleteSiteVisit';
import UpdatePayment from './pages/UpdatePayment';
import Expenses from './pages/Expenses';
import ExpenseForm from './pages/ExpenseForm';
import ExpenseReports from './pages/ExpenseReports';
import RevenueReports from './pages/RevenueReports';

import TechnicianLiveMap from './pages/TechnicianLiveMap';
import TechnicianPortal from './pages/TechnicianPortal';
import './assets/styles/index.css';

import ProfileSettings from './pages/ProfileSettings';
import Invoices from './pages/Invoices';
import InvoiceView from './pages/InvoiceView';
import InvoiceEdit from './pages/InvoiceEdit';
import Subscribe from './pages/Subscribe';
// import AllTechniciansPerformance from './pages/AllTechniciansPerformance';
// import TechnicianPerformance from './pages/TechnicianPerformance';

// Layout component for authenticated pages
const Layout = ({ children }) => {
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="app-content">
        <Header />
        <main className="app-main">{children}</main>
      </div>
    </div>
  );
};

// Protected Route wrapper – requires login and subscription access (except on /subscribe)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, hasSubscriptionAccess, loading } = useAuth();
  const pathname = window.location.pathname;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (pathname === '/subscribe') {
    return <Subscribe />;
  }

  if (!hasSubscriptionAccess) {
    return <Navigate to="/subscribe" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route wrapper (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, hasSubscriptionAccess, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={hasSubscriptionAccess ? '/dashboard' : '/subscribe'} replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* Subscribe – requires login, no layout */}
      <Route
        path="/subscribe"
        element={
          <ProtectedRoute>
            <Subscribe />
          </ProtectedRoute>
        }
      />

      {/* Technician Mobile Portal (Accessible on mobile browser / PWA) */}
      <Route path="/tech-portal" element={<TechnicianPortal />} />


      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Technicians Routes */}
      <Route
        path="/technicians"
        element={
          <ProtectedRoute>
            <Technicians />
          </ProtectedRoute>
        }
      />

      {/* Customers Routes */}
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <Customers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/new"
        element={
          <ProtectedRoute>
            <CustomerForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:id"
        element={
          <ProtectedRoute>
            <CustomerView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:id/edit"
        element={
          <ProtectedRoute>
            <CustomerForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/technicians/map"
        element={
          <ProtectedRoute>
            <TechnicianLiveMap />
          </ProtectedRoute>
        }
      />

      <Route
        path="/technicians/new"
        element={
          <ProtectedRoute>
            <TechnicianForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/technicians/:id"
        element={
          <ProtectedRoute>
            <TechnicianView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/technicians/:id/edit"
        element={
          <ProtectedRoute>
            <TechnicianForm />
          </ProtectedRoute>
        }
      />

      {/* Helpers Routes */}
      <Route
        path="/helpers"
        element={
          <ProtectedRoute>
            <Helpers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/helpers/new"
        element={
          <ProtectedRoute>
            <HelperForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/helpers/:id"
        element={
          <ProtectedRoute>
            <HelperView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/helpers/:id/edit"
        element={
          <ProtectedRoute>
            <HelperForm />
          </ProtectedRoute>
        }
      />

      {/* Inventory Routes */}
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <Inventory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/new"
        element={
          <ProtectedRoute>
            <InventoryForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/:id/edit"
        element={
          <ProtectedRoute>
            <InventoryForm />
          </ProtectedRoute>
        }
      />

      {/* Tools Routes */}
      <Route
        path="/tools"
        element={
          <ProtectedRoute>
            <Tools />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/new"
        element={
          <ProtectedRoute>
            <ToolForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/:id/edit"
        element={
          <ProtectedRoute>
            <ToolForm />
          </ProtectedRoute>
        }
      />

      {/* Site Visits Routes */}
      <Route
        path="/site-visits"
        element={
          <ProtectedRoute>
            <SiteVisits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/site-visits/:id/edit"
        element={
          <ProtectedRoute>
            <SiteVisitForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/site-visits/new"
        element={
          <ProtectedRoute>
            <SiteVisitForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/site-visits/:id"
        element={
          <ProtectedRoute>
            <SiteVisitView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/site-visits/:id/complete"
        element={
          <ProtectedRoute>
            <CompleteSiteVisit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/site-visits/:id/payment"
        element={
          <ProtectedRoute>
            <UpdatePayment />
          </ProtectedRoute>
        }
      />

      {/* Expenses Routes */}
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
            <Expenses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses/new"
        element={
          <ProtectedRoute>
            <ExpenseForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses/:id/edit"
        element={
          <ProtectedRoute>
            <ExpenseForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses/reports"
        element={
          <ProtectedRoute>
            <ExpenseReports />
          </ProtectedRoute>
        }
      />

      {/* Invoices Routes */}
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <Invoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/revenue"
        element={
          <ProtectedRoute>
            <RevenueReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:id/edit"
        element={
          <ProtectedRoute>
            <InvoiceEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:id"
        element={
          <ProtectedRoute>
            <InvoiceView />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <ProfileSettings />
          </ProtectedRoute>
        }
      />

      {/* Default Route */}
      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
